import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBsView } from '../../api/financials_bs';
import Card from '../../components/Card';
import i18next from 'i18next';
import { tp } from '../../i18n/useP';

/* Vue « Financement » du cockpit — structure de financement (composition du
   passif, réel vs prévision) + contrôles légaux art. 725a (perte de capital) et
   725b (surendettement) lus sur la photo des fonds propres (réel ET projeté).
   Le 3e contrôle 725 (menace d'insolvabilité, prospectif) n'est PAS ici : il
   relève du cash prévisionnel, pas du bilan.

   Capital protégé = capital-actions + réserves légales (parts non remboursables)
   → base exacte du seuil 725a (50 % du capital protégé), depuis la scission
   réserves légales / libres. Aucun chiffre fabriqué : donnée absente = affichée
   absente (—). */

const STD_PROTECTED = ['share_capital', 'legal_reserves'];

export default function CockpitFinancement({ tenantId, year, unit = 'kchf' }) {
  const { t } = useTranslation();
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const div = unit === 'chf' ? 1 : 1000;

  useEffect(() => {
    if (!tenantId || !year) { setView(null); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    getBsView(tenantId, year, { flatForecast: true })
      .then((res) => { if (alive) { setView(res?.data || res || null); setLoading(false); } })
      .catch(() => { if (alive) { setView(null); setLoading(false); } });
    return () => { alive = false; };
  }, [tenantId, year]);

  const model = useMemo(() => (view ? buildModel(view) : null), [view]);

  const money = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return '—';
    const n = Math.round(Number(v) / div);
    const s = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '’');
    return (n < 0 ? '−' : '') + s;
  };
  const unitNote = <span className="ckpit__unit-note">{unit === 'chf' ? 'CHF' : 'kCHF'}</span>;

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: '18px 0' }}>{t('phrases:p_01cba1dfba')}</p>;
  if (!model) return <p style={{ color: 'var(--color-text-muted)', padding: '18px 0' }}>{t('phrases:p_76210bbe36')}</p>;

  const { struct, kpis, feux, hasReal } = model;

  return (
    <div className="ckfin-grid">
      <Card title={tp('phrases:p_42d0fba2f4')} actions={unitNote}>
        <p className="ckfin-seclbl">{tp('phrases:p_ac82d32486')}</p>
        {struct.map((b) => (
          <div className="ckfin-bar-row" key={b.key}>
            <div className="ckfin-bar-cap"><span>{b.label}</span><b>{money(b.total)}</b></div>
            <div className="ckfin-bar">
              {b.parts.map((part) => (part.pct > 0.01 ? (
                <span key={part.key} className={`ckfin-seg ckfin-seg--${part.key}`} style={{ width: `${part.pct}%` }}>
                  {part.pct >= 12 ? `${part.short} ${fmtPct(part.pct)}` : ''}
                </span>
              ) : null))}
            </div>
          </div>
        ))}
        <div className="ckfin-legend">
          <span><i className="ckfin-seg--fp" />{t('phrases:p_09cdff9ffd')}</span>
          <span><i className="ckfin-seg--lt" />{t('phrases:p_025e39d855')}</span>
          <span><i className="ckfin-seg--ct" />{t('phrases:p_af5e4fbb8d')}</span>
        </div>

        <div className="ckfin-kpis">
          {kpis.map((k) => (
            <div className="ckfin-kpi" key={k.key}>
              <div className="ckfin-kpi-lbl">{k.label}</div>
              <div className="ckfin-kpi-vals">
                <span className="ckfin-kpi-real">{k.real}</span>
                {k.prev != null && (
                  <span className="ckfin-kpi-prev">
                    <span className={`ckfin-arw ckfin-arw--${k.trend}`}>{k.arrow}</span> {k.prev} {t('phrases:p_c177c4172d')}
                  </span>
                )}
              </div>
              <div className="ckfin-kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card title={t('phrases:p_b75dc54860')} actions={<span className="ckpit__unit-note">{t('phrases:p_01eb9e6ab8')}</span>}>
        {!hasReal && (
          <p className="ckfin-seclbl" style={{ marginTop: 2 }}>{t('phrases:p_921172e1f3')}</p>
        )}
        {feux.map((f) => (
          <div className="ckfin-feu" key={f.key}>
            <span className={`ckfin-dot ckfin-dot--${f.status}`} />
            <div>
              <div className="ckfin-feu-top">
                <span className="ckfin-feu-name">{f.name}</span>
                <span className="ckfin-feu-art">{f.art}</span>
                <span className={`ckfin-feu-pill ckfin-feu-pill--${f.status}`}>{f.verdict}</span>
              </div>
              <div className="ckfin-feu-rule" dangerouslySetInnerHTML={{ __html: f.rule }} />
              <div className="ckfin-feu-cols">
                {f.cols.map((c) => (
                  <div className="ckfin-feu-col" key={c.key}>
                    <div className="ckfin-fc-l">{c.label}</div>
                    <div className="ckfin-fc-v">{c.value}</div>
                    <div className={`ckfin-fc-m ckfin-fc-m--${c.status}`}>{c.note}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
        <div className="ckfin-cascade">
          {t('phrases:p_3d165ba295')} <span className="ckfin-c--g">&#9679; {t('phrases:p_0e0b220e0a')}</span> &nbsp;&#8250;&nbsp;
          <span className="ckfin-c--o">&#9679; {t('phrases:p_5067b8a355')}</span> &nbsp;&#8250;&nbsp;
          <span className="ckfin-c--r">&#9679; {t('phrases:p_31f24dd56e')}</span>.
          {t('phrases:p_c41e15ec6e')}
        </div>
      </Card>
    </div>
  );
}

function fmtPct(v) { return `${v.toFixed(1).replace('.', ',')} %`; }
function pct1(v) { return v == null ? '—' : `${(v * 100).toFixed(1).replace('.', ',')} %`; }
function mult(v) { return v == null ? '—' : `${v.toFixed(2).replace('.', ',')}×`; }

/* Statuts légaux (g = sain, o = perte de capital, r = surendettement). */
function s725a(fp, cp) {
  if (fp == null || cp == null) return null;
  if (fp < 0) return 'r';
  return fp >= 0.5 * cp ? 'g' : 'o';
}
function s725b(fp) { return fp == null ? null : (fp >= 0 ? 'g' : 'r'); }
const RANK = { g: 0, o: 1, r: 2 };
const worst = (a, b) => (RANK[b] > RANK[a] ? b : a);

function buildModel(view) {
  const rows = view.rows || [];
  const cc = Number.isFinite(view.closed_count) ? view.closed_count : 12;
  const hasReal = cc > 0;
  const rM = hasReal ? cc : null;                     // mois réel = dernière clôture
  const byCode = (code) => rows.find((r) => r.code === code);
  const at = (code, mth) => { const r = byCode(code); return r && r.months ? Number(r.months[String(mth)] || 0) : null; };
  const protectedAt = (mth) => rows
    .filter((r) => STD_PROTECTED.includes(r.std_key))
    .reduce((s, r) => s + Number(r.months?.[String(mth)] || 0), 0);

  const pick = (fn) => ({ r: rM ? fn(rM) : null, p: fn(12) });
  const fp = pick((mth) => at('sec:equity', mth));
  const ct = pick((mth) => at('sec:current_liabilities', mth));
  const lt = pick((mth) => at('sec:long_term_liabilities', mth));
  const immo = pick((mth) => at('sec:fixed_assets', mth));
  const cp = pick(protectedAt);
  const passif = { r: sumOrNull(fp.r, lt.r, ct.r), p: sumOrNull(fp.p, lt.p, ct.p) };

  // Barres de structure : réel + prévision.
  const barsSrc = [];
  if (hasReal) barsSrc.push({ key: 'reel', label: tp('phrases:p_9ddab5c801'), fp: fp.r, lt: lt.r, ct: ct.r, total: passif.r });
  barsSrc.push({ key: 'prev', label: tp('phrases:p_5327e8e6f1'), fp: fp.p, lt: lt.p, ct: ct.p, total: passif.p });
  const struct = barsSrc.map((b) => ({
    key: b.key, label: b.label, total: b.total,
    parts: [
      { key: 'fp', short: tp('phrases:p_7def6da18b'), pct: pctOf(b.fp, b.total) },
      { key: 'lt', short: tp('phrases:p_4d8cb46818'), pct: pctOf(b.lt, b.total) },
      { key: 'ct', short: tp('phrases:p_1bda2be1e4'), pct: pctOf(b.ct, b.total) },
    ],
  }));

  // KPI de structure (réel + prévision).
  const tauxFp = { r: ratio(fp.r, passif.r), p: ratio(fp.p, passif.p) };
  const endett = { r: ratio(sumOrNull(ct.r, lt.r), fp.r), p: ratio(sumOrNull(ct.p, lt.p), fp.p) };
  const couv = { r: ratio(sumOrNull(fp.r, lt.r), immo.r), p: ratio(sumOrNull(fp.p, lt.p), immo.p) };

  const kpis = [
    kpi('taux', tp('phrases:p_69ac28f489'), pct1(tauxFp.r), tauxFp.p != null ? pct1(tauxFp.p) : null, trend(tauxFp.r, tauxFp.p, true), tp('phrases:p_40a790ec10')),
    kpi('end', tp('phrases:p_5bb2b41891'), mult(endett.r), endett.p != null ? mult(endett.p) : null, trend(endett.r, endett.p, false), tp('phrases:p_3dbafe478d')),
    kpi('couv', tp('phrases:p_b31c67eaab'), mult(couv.r), couv.p != null ? mult(couv.p) : null, trend(couv.r, couv.p, true), tp('phrases:p_c61e71bb49')),
    kpiCp(cp),
  ];

  // Feux 725a / 725b : réel + prévision, dot = pire des deux.
  const feuA = feu('a', tp('phrases:p_a74dd6b074'), 'art. 725a CO',
    i18next.t('phrases:p_6b495cb1d8', { threshold: fmtChf(0.5 * (cp.r != null ? cp.r : cp.p)) }),
    [colA(tp('phrases:p_963e56fe84'), fp.r, cp.r, rM), colA(tp('phrases:p_850ac9fd0b'), fp.p, cp.p, 12)], s725a);
  const feuB = feu('b', tp('phrases:p_379d8b779e'), 'art. 725b CO',
    tp('phrases:p_a255be13af'),
    [colB(tp('phrases:p_963e56fe84'), fp.r), colB(tp('phrases:p_850ac9fd0b'), fp.p)], s725b);

  return { struct, kpis, feux: [feuA, feuB], hasReal };
}

/* ── helpers de construction ─────────────────────────────────────────────── */
function kpi(key, label, real, prev, tr, sub) {
  const arrow = tr === 'up' ? '↑' : tr === 'down' ? '↓' : '→';
  return { key, label, real, prev, trend: tr, arrow, sub };
}
function kpiCp(cp) {
  const real = cp.r != null ? fmtChf(cp.r) : (cp.p != null ? fmtChf(cp.p) : '—');
  const stable = cp.r != null && cp.p != null && Math.abs(cp.r - cp.p) < 1;
  return { key: 'cp', label: tp('phrases:p_a491fd5aac'), real,
    prev: stable ? tp('phrases:p_e2000d7556') : (cp.p != null ? fmtChf(cp.p) : null), trend: 'flat', arrow: '', sub: tp('phrases:p_c72238f559') };
}
function feu(key, name, art, rule, cols, statusFn) {
  const status = cols.map((c) => c.status).filter(Boolean).reduce((a, b) => worst(a, b), 'g');
  const verdict = status === 'g' ? tp('phrases:p_0de13ff438') : (key === 'a' && status === 'o') ? tp('phrases:p_a74dd6b074') : tp('phrases:p_379d8b779e');
  return { key, name, art, rule, cols, status, verdict };
}
function colA(label, fp, cp, month) {
  if (fp == null || cp == null) return { key: label, label, value: '—', note: '', status: null };
  const st = s725a(fp, cp);
  const marge = fp - 0.5 * cp;
  return { key: label, label, value: fmtChf(fp),
    note: st === 'g' ? i18next.t('phrases:p_8647a8c659', { amount: fmtChf(marge) }) : st === 'o' ? i18next.t('phrases:p_15abb4b697', { amount: fmtChf(marge) }) : tp('phrases:p_1c08c089d7'),
    status: st };
}
function colB(label, fp) {
  if (fp == null) return { key: label, label, value: '—', note: '', status: null };
  const st = s725b(fp);
  return { key: label, label, value: fmtChf(fp), note: st === 'g' ? tp('phrases:p_557f42dd79') : tp('phrases:p_2e2d55f11c'), status: st };
}
function trend(r, p, higherIsBetter) {
  if (r == null || p == null) return 'flat';
  if (Math.abs(p - r) < 1e-9) return 'flat';
  const rising = p > r;
  return (rising === higherIsBetter) ? 'up' : 'down';
}
function sumOrNull(...vs) {
  if (vs.some((v) => v == null)) return null;
  return vs.reduce((a, v) => a + v, 0);
}
function pctOf(v, total) { return (v == null || !total) ? 0 : (v / total) * 100; }
function ratio(a, b) { return (a == null || b == null || Math.abs(b) < 1e-9) ? null : a / b; }

/* kCHF compact (apostrophe suisse) — pour seuils et marges des feux (indépendant
   de l'unité d'affichage : les montants légaux se lisent mieux en kCHF). */
function fmtChf(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v) / 1000);
  const s = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '’');
  return (n < 0 ? '−' : '') + s + ' kCHF';
}
